import { CodeLang } from '@common/codemirror/types/code-lang.type';
import { stripBOM } from '@common/html/util/xml';
import { ProjectFileType } from '@common/project-files/enums/project-file-type.enum';
import { CSSBeautifyOptions, css_beautify, HTMLBeautifyOptions, html_beautify, JSBeautifyOptions, js_beautify } from 'js-beautify';

const FlareContentUnformatted = [
  'pre',
  'textarea',
  'madcap:annotation',
  'madcap:codesnippetbody',
  'madcap:popupbody'
];

const FlareInlineElements = [
  'a', 'abbr', 'area', 'audio', 'b', 'bdi', 'bdo', 'br', 'button', 'canvas', 'cite',
  'code', 'data', 'datalist', 'del', 'dfn', 'em', 'embed', 'i', 'iframe', 'img',
  'input', 'ins', 'kbd', 'keygen', 'label', 'map', 'mark', 'math', 'meter', 'noscript',
  'object', 'output', 'progress', 'q', 'ruby', 's', 'samp', /* 'script', */ 'select', 'small',
  'span', 'strong', 'sub', 'sup', 'svg', 'template', 'textarea', 'time', 'u', 'var',
  'video', 'wbr', 'text',
  // obsolete inline tags
  'acronym', 'big', 'strike', 'tt',
  // custom inline tags
  'madcap:annotation', 'madcap:change', 'madcap:concept', 'madcap:conceptlink', 'madcap:conditionaltext',
  'madcap:equation', 'madcap:expanding', 'madcap:expandingbody', 'madcap:expandinghead', 'madcap:footnote',
  'madcap:glossaryterm', 'madcap:keyword', 'madcap:keywordlink', 'madcap:model3d', 'madcap:nameddestination',
  'madcap:popup', 'madcap:popupbody', 'madcap:popuphead', 'madcap:qrcode', 'madcap:relatedtopics',
  'madcap:responsivecontenttext', 'madcap:responsivecontentitem', 'madcap:shortcut', 'madcap:toggler',
  'madcap:variable', 'madcap:snippettext', 'madcap:xref'
];

/** Formats the code of the project file using its type to define the code language and additional formatting options. */
export function formatCodeByFileType(code: string, fileType: ProjectFileType): string {
  let lang: CodeLang;
  switch (fileType) {
    case ProjectFileType.Flare:
      lang = 'flare';
      break;
    case ProjectFileType.ConditionTagSet:
    case ProjectFileType.Target:
    case ProjectFileType.Toc:
    case ProjectFileType.VariableSet:
    case ProjectFileType.Destination:
    case ProjectFileType.Project:
      lang = 'xml';
      break;
    case ProjectFileType.Stylesheet:
      lang = 'css';
      break;
    default:
      return code; // Formatting is not available for other project file types
  }

  let options: CSSBeautifyOptions | HTMLBeautifyOptions | JSBeautifyOptions;
  if (fileType === ProjectFileType.Target ||
    fileType === ProjectFileType.Destination ||
    fileType === ProjectFileType.Project) {
    options = {
      wrap_attributes: 'force',
      wrap_attributes_indent_size: 2,
    };
  }

  return formatCodeByLang(code, lang, options);
}

/** Formats the code using the specified code language and formatting options. */
export function formatCodeByLang(code: string, lang: CodeLang, options?: CSSBeautifyOptions | HTMLBeautifyOptions | JSBeautifyOptions): string {
  let beautifyOptions: CSSBeautifyOptions | HTMLBeautifyOptions | JSBeautifyOptions = {
    indent_size: 2,
    preserve_newlines: false, // Don't use pre-existing newlines because we want this to consistently create the same string from the same markup
    wrap_line_length: 0 // Disables wrapping
  };

  if (lang === 'flare') {
    (beautifyOptions as HTMLBeautifyOptions).content_unformatted = FlareContentUnformatted;
    (beautifyOptions as HTMLBeautifyOptions).inline = FlareInlineElements;
  } else if (lang === 'css') {
    // Preserve new lines in css to be more consistent with Flare's stylesheet markup
    (beautifyOptions as CSSBeautifyOptions).preserve_newlines = true;
    (beautifyOptions as CSSBeautifyOptions).max_preserve_newlines = 2;
  }

  // Mixin the given options
  beautifyOptions = {
    ...beautifyOptions,
    ...options
  };

  // Strip the BOM first as it will interfere with formatting
  switch (lang) {
    case 'css':
      return css_beautify(stripBOM(code), beautifyOptions);
    case 'flare':
    case 'html':
    case 'xml':
      return html_beautify(stripBOM(code), beautifyOptions);
    case 'javascript':
    case 'json':
      return js_beautify(stripBOM(code), beautifyOptions);
    default:
      return code;
  }
}
